import { Box, Image, ResponsiveValue, SystemStyleObject } from "@chakra-ui/react";
import {
  ActionMeta,
  chakraComponents,
  Select as ReactSelect,
  SingleValue,
} from "chakra-react-select";
import {
  menuPortalStyles,
  dropdownIndicatorStyle,
  indicatorSeparatorStyle,
  baseOptionStyle,
  placeholderStyle,
  valueContainerStyle,
} from "./styles";

export type Option = {
  value: string | number;
  label: string;
  icon?: string;
};

export type SelectProps = {
  options?: Option[];
  value?: Option | null;
  onChange?: (
    newValue: SingleValue<Option>,
    actionMeta: ActionMeta<Option>
  ) => void;
  isValid?: boolean;
  placeholder?: string;
  width?: ResponsiveValue<string>;
  menuPortalTarget?: HTMLElement;
  isDisabled?: boolean;
  isClearable?: boolean;
  "aria-label"?: string;
  chakraStyles?: {
    placeholder?: (provided: SystemStyleObject) => SystemStyleObject;
  };
};

const Select = ({
  options,
  value,
  onChange,
  isValid = true,
  placeholder,
  width,
  isDisabled,
  isClearable,
  "aria-label": ariaLabel,
  chakraStyles: chakraStylesOverride,
}: SelectProps) => {
  return (
    <ReactSelect
      value={value}
      options={options}
      onChange={onChange}
      isDisabled={isDisabled}
      isClearable={isClearable}
      aria-label={ariaLabel}
      menuPortalTarget={document.body}
      styles={menuPortalStyles}
      chakraStyles={{
        control: (provided) => ({
          ...provided,
          width: width ?? "auto",
          bg: "white",
          borderColor: isValid ? provided.borderColor : "error.500",
          _hover: {
            borderColor: isValid ? provided.borderColor : "error.500",
          },
          _focus: {
            borderColor: isValid ? provided.borderColor : "error.500",
            shadow: "none",
          },
          cursor: "pointer",
        }),
        dropdownIndicator: dropdownIndicatorStyle,
        indicatorSeparator: indicatorSeparatorStyle,
        option: (provided, state) => ({
          ...baseOptionStyle(provided, state.isSelected),
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }),
        placeholder: (provided) => {
          const base = placeholderStyle(provided);
          return chakraStylesOverride?.placeholder
            ? chakraStylesOverride.placeholder(base)
            : base;
        },
        singleValue: (provided) => ({
          ...provided,
          marginLeft: "6px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }),
        valueContainer: valueContainerStyle,
      }}
      placeholder={placeholder}
      components={{
        Option: ({ children, ...props }) => (
          <chakraComponents.Option {...props}>
            <Box overflow="hidden" textOverflow="ellipsis">
              {children}
            </Box>
            {props.data.icon && (
              <Image src={props.data.icon} boxSize="20px" pl="2px" />
            )}
          </chakraComponents.Option>
        ),
        SingleValue: ({ children, ...props }) => (
          <chakraComponents.SingleValue {...props}>
            <Box overflow="hidden" textOverflow="ellipsis">
              {children}
            </Box>
            {props.data.icon && (
              <Image src={props.data.icon} boxSize="20px" pl="2px" />
            )}
          </chakraComponents.SingleValue>
        ),
      }}
    />
  );
};

export default Select;
